import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Icon, Menus, Text, useTheme } from '@Sage/hoi-poi-ui';
import { downloadFile, sanitizeFilename } from '@Sage/ssm-utils';

import { STATUS, SAVE_MODES, useTopolEditor } from './hooks/useTopolEditor';
import useTopolVariables from './hooks/useTopolVariables';
import Loading from 'components/Loading';
import TitleInput from '../MarketingCampaignsForm/TitleInput';
import CampaignPreviewTestModal from '../MarketingCampaignsForm/CampaignPreviewTestModal';
import { getLiteral } from 'utils/getLiteral';
import './styles.scss';

let instanceCounter = 0;

const EmailEditor = memo(
    ({
        isOpen = false,
        onSave,
        onExit,
        onPreview,
        onSaveAsTemplate,
        emailConfig = '',
        title = '',
        editableTitle = false,
        onTitleChange,
        footerPreview,
    }) => {
        const theme = useTheme();
        const uniqueUserId = useSelector((state) => {
            const { idUsuario, idImplementacion } = state.config?.userData;
            return `${idUsuario}_${idImplementacion}`;
        });
        const language = useSelector((state) => state.config?.userData?.langISOInterface || 'en');
        const [currentTitle, setCurrentTitle] = useState(title);
        const [isBuiltInPreviewOpen, setIsBuiltInPreviewOpen] = useState(false);
        const [builtInPreviewHtml, setBuiltInPreviewHtml] = useState('');
        const containerRef = useRef(null);
        // Generate unique ID for each instance to avoid DOM conflicts
        const containerId = useMemo(() => {
            instanceCounter += 1;
            return `topol-app-${instanceCounter}-${Date.now()}`;
        }, []);

        const {
            variables,
            loading: variablesLoading,
            error,
            retry,
        } = useTopolVariables({ isOpen });

        // Sync currentTitle when title prop changes
        useMemo(() => {
            setCurrentTitle(title);
        }, [title]);

        const handleTitleChange = useCallback(
            (newTitle) => {
                setCurrentTitle(newTitle);
                onTitleChange?.(newTitle);
            },
            [onTitleChange],
        );

        const handlePreviewRequest = useCallback(
            ({ html } = {}) => {
                if (onPreview) {
                    onPreview({ html, title: currentTitle });
                    return;
                }

                setBuiltInPreviewHtml(html ?? '');
                setIsBuiltInPreviewOpen(true);
            },
            [currentTitle, onPreview],
        );

        const handleDownloadHtmlExport = useCallback(
            ({ html } = {}) => {
                const filename = sanitizeFilename(currentTitle.trim() || 'campaign');
                downloadFile(html ?? '', `${filename}.html`, 'text/html');
            },
            [currentTitle],
        );

        const handleCloseBuiltInPreview = useCallback(() => {
            setIsBuiltInPreviewOpen(false);
        }, []);

        const builtInPreviewSubject = useMemo(() => {
            return currentTitle.trim() || getLiteral('label_email_template');
        }, [currentTitle]);

        const {
            status,
            pendingSaveMode,
            retry: retryEditor,
            save,
        } = useTopolEditor({
            containerRef,
            containerId,
            config: emailConfig,
            theme,
            onSave,
            onPreview: handlePreviewRequest,
            onSaveAsTemplate,
            onDownloadHtml: handleDownloadHtmlExport,
            isOpen,
            variables,
            userId: uniqueUserId,
            language,
            footerPreviewValues: footerPreview,
        });

        const handleExitWithoutSaving = useCallback(() => {
            onExit?.();
        }, [onExit]);

        const areSaveActionsDisabled =
            status !== STATUS.READY || variablesLoading || pendingSaveMode !== null;
        const isSaving = pendingSaveMode === SAVE_MODES.SAVE;

        const handleSaveAsTemplate = useCallback(() => {
            if (!onSaveAsTemplate || areSaveActionsDisabled) {
                return;
            }
            save(SAVE_MODES.SAVE_AS_TEMPLATE);
        }, [areSaveActionsDisabled, onSaveAsTemplate, save]);

        const handleDownloadHtml = useCallback(() => {
            if (areSaveActionsDisabled) {
                return;
            }
            save(SAVE_MODES.DOWNLOAD);
        }, [areSaveActionsDisabled, save]);

        const menuItems = useMemo(() => {
            const items = [];

            if (onSaveAsTemplate) {
                items.push({
                    title: getLiteral('label_marketing_campaigns_design_save_as_template'),
                    icon: <Icon name="emailStar" />,
                    onClick: handleSaveAsTemplate,
                });
            }

            items.push({
                title: getLiteral('label_marketing_campaigns_design_download_html'),
                icon: <Icon name="downloadAlt" />,
                onClick: handleDownloadHtml,
            });

            items.push({
                title: getLiteral('action_exit_without_saving'),
                icon: <Icon name="logout" />,
                onClick: handleExitWithoutSaving,
            });

            return items;
        }, [handleDownloadHtml, handleExitWithoutSaving, handleSaveAsTemplate, onSaveAsTemplate]);

        if (!isOpen) {
            return null;
        }

        return (
            <div className="email-campaigns">
                <div className="email-campaigns__topbar">
                    <div className="email-campaigns__topbar-left">
                        {editableTitle ? (
                            <TitleInput
                                title={currentTitle}
                                onChange={handleTitleChange}
                                onSave={handleTitleChange}
                            />
                        ) : (
                            !!currentTitle?.trim() && (
                                <Text
                                    type="h6"
                                    className="email-campaigns__campaign-title"
                                    isTruncated
                                    useTooltip
                                >
                                    {currentTitle}
                                </Text>
                            )
                        )}
                    </div>
                    <div className="email-campaigns__topbar-right">
                        <Button
                            type="secondary"
                            onClick={() => save(SAVE_MODES.PREVIEW)}
                            isDisabled={areSaveActionsDisabled}
                        >
                            {getLiteral('label_marketing_campaigns_preview_and_test')}
                        </Button>
                        <Button
                            onClick={() => save(SAVE_MODES.SAVE)}
                            isLoading={isSaving}
                            isDisabled={areSaveActionsDisabled}
                        >
                            {getLiteral('action_save_and_exit')}
                        </Button>
                        <Menus
                            size="small"
                            placement="bottom-end"
                            items={menuItems}
                            classNameMenu="email-campaigns__menu-popover"
                            icon={<Icon name="threeDots" />}
                        />
                    </div>
                </div>
                {error && (
                    <div className="email-campaigns__error">
                        <Text type="body" color="danger">
                            {getLiteral('error_unexpected')}
                        </Text>
                        <Button onClick={retry} color="primary">
                            {getLiteral('label_retry')}
                        </Button>
                    </div>
                )}

                {((status === STATUS.INITIALIZING && !variablesLoading) || variablesLoading) && (
                    <div className="email-campaigns__loading">
                        <Loading />
                    </div>
                )}

                {status === STATUS.ERROR && !error && (
                    <div className="email-campaigns__error">
                        <Text type="body" color="danger">
                            {getLiteral('error_unexpected')}
                        </Text>
                        <Button onClick={retryEditor} color="primary">
                            {getLiteral('label_retry')}
                        </Button>
                    </div>
                )}

                <div
                    id={containerId}
                    ref={containerRef}
                    className={classnames('email-campaigns__editor', {
                        'email-campaigns__editor--ready': status === STATUS.READY,
                    })}
                />
                <CampaignPreviewTestModal
                    isOpen={isBuiltInPreviewOpen}
                    onRequestClose={handleCloseBuiltInPreview}
                    from=""
                    subject={builtInPreviewSubject}
                    previewText=""
                    htmlPreview={builtInPreviewHtml}
                    defaultRecipient=""
                />
            </div>
        );
    },
);

EmailEditor.displayName = 'EmailEditor';

EmailEditor.propTypes = {
    isOpen: PropTypes.bool,
    onSave: PropTypes.func,
    onExit: PropTypes.func,
    onPreview: PropTypes.func,
    onSaveAsTemplate: PropTypes.func,
    emailConfig: PropTypes.string,
    title: PropTypes.string,
    editableTitle: PropTypes.bool,
    onTitleChange: PropTypes.func,
    footerPreview: PropTypes.shape({
        senderName: PropTypes.string,
        senderAddress: PropTypes.string,
        footerHere: PropTypes.string,
        linkHref: PropTypes.string,
    }),
};

export default EmailEditor;
